import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Container, 
  Paper, 
  Typography, 
  Box, 
  Grid, 
  Card, 
  IconButton, 
  CircularProgress,
  Alert,
  Avatar,
  Divider
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { api } from '../context/AuthContext';
import { format } from 'date-fns';
import ImageWithRetry from '../components/ImageWithRetry';
import ErrorBoundary from '../components/ErrorBoundary';
import { Helmet } from 'react-helmet';

const ViewMemorial = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [memorial, setMemorial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [photos, setPhotos] = useState([]);
  const [imagesPreloaded, setImagesPreloaded] = useState(false);
  
  // Get API base URL - should be https://memorylink.onrender.com
  const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
  
  console.log('ViewMemorial using API base URL:', apiBaseUrl);

  const goBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const formatDate = useCallback((date) => {
    if (!date) return '';
    try {
      return format(new Date(date), 'MMM d, yyyy');
    } catch (error) {
      console.error('Date formatting error:', error);
      return date;
    }
  }, []);

  const logError = useCallback((type, error, details) => {
    console.error(`[${type}] Error:`, {
      error,
      details,
      timestamp: new Date().toISOString(),
      memorialId: id
    });
  }, [id]);

  const ensureAbsoluteUrl = useCallback((url) => {
    if (!url) return '';
    
    // If it's already an absolute URL
    if (url.startsWith('http')) {
      return url;
    }
    
    // If it already includes the API base URL
    if (url.includes(apiBaseUrl)) {
      return url;
    }
    
    // If it's a relative URL, add the API base URL
    return `${apiBaseUrl}${url}`;
  }, [apiBaseUrl]);

  const preloadImages = useCallback(async (imageUrls) => {
    try {
      console.log('Preloading images:', imageUrls);
      const preloadPromises = imageUrls.map(url => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => {
            console.log(`Preloaded: ${url}`);
            resolve();
          };
          img.onerror = (e) => {
            console.error(`Failed to preload: ${url}`, e);
            reject(e);
          };
          img.src = url;
        });
      });

      await Promise.allSettled(preloadPromises);
      setImagesPreloaded(true);
    } catch (error) {
      logError('Preload', error, { imageUrls });
      // Even if preloading fails, still set to true to show fallback
      setImagesPreloaded(true);
    }
  }, [logError]);

  useEffect(() => {
    const fetchMemorial = async () => {
      try {
        setLoading(true);
        console.log(`Fetching memorial with ID: ${id} from ${apiBaseUrl}/api/memorials/${id}`);
        
        const response = await api.get(`/api/memorials/${id}`);
        
        let memorialData;
        if (response.data && response.data.memorial) {
          memorialData = response.data.memorial;
        } else if (response.data) {
          memorialData = response.data;
        } else {
          throw new Error('Invalid response format from server');
        }
        
        // Process photo URLs to ensure absolute paths
        if (memorialData.photos && memorialData.photos.length > 0) {
          console.log('Original photo URLs:', memorialData.photos.map(p => p.url));
          
          memorialData.photos = memorialData.photos.map(photo => ({
            ...photo,
            url: ensureAbsoluteUrl(photo.url)
          }));
          
          console.log('Processed photo URLs:', memorialData.photos.map(p => p.url));
        }
        
        setMemorial(memorialData);
        setPhotos(memorialData.photos || []);

        if (memorialData.photos?.length > 0) {
          const imageUrls = memorialData.photos.map(photo => photo.url);
          await preloadImages(imageUrls);
        } else {
          setImagesPreloaded(true);
        }
      } catch (err) {
        logError('Fetch', err, { endpoint: `/api/memorials/${id}` });
        setError(`Failed to load memorial: ${err.response?.data?.message || err.message}`);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchMemorial();
    } else {
      setError('No memorial ID provided');
      setLoading(false);
    }
  }, [id, apiBaseUrl, logError, preloadImages, ensureAbsoluteUrl]);

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading memorial...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <IconButton onClick={goBack}>
            <ArrowBackIcon />
            <Typography sx={{ ml: 1 }}>Go Back</Typography>
          </IconButton>
        </Box>
      </Container>
    );
  }

  if (!memorial) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="warning">Memorial not found</Alert>
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <IconButton onClick={goBack}>
            <ArrowBackIcon />
            <Typography sx={{ ml: 1 }}>Go Back</Typography>
          </IconButton>
        </Box>
      </Container>
    );
  }

  const profilePhoto = photos && photos.length > 0 ? photos[0].url : 'https://via.placeholder.com/150';

  return (
    <ErrorBoundary fallback="Error loading memorial page">
      <Helmet>
        <title>{memorial?.name || 'Memorial'} | Digital Memorials UK</title>
        <meta name="description" content={`View the digital memorial for ${memorial?.name || 'a loved one'}. Photos and memories preserved for generations.`} />
        <link rel="canonical" href={`https://digitalmemorials.co.uk/memorial/${id}`} />
        
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "ProfilePage",
              "dateCreated": "${memorial?.createdAt || ''}",
              "mainEntity": {
                "@type": "Person",
                "name": "${memorial?.name || ''}",
                "birthDate": "${memorial?.dateOfBirth || ''}",
                "deathDate": "${memorial?.dateOfDeath || ''}",
                "description": "${memorial?.biography?.substring(0, 150) || ''}${memorial?.biography?.length > 150 ? '...' : ''}"
              }
            }
          `}
        </script>
      </Helmet>

      <Container maxWidth="md" sx={{ py: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <IconButton onClick={goBack}>
            <ArrowBackIcon />
          </IconButton>
        </Box>

        <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
          {/* Profile Header */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' }, 
            alignItems: { xs: 'center', sm: 'flex-start' }, 
            mb: 3 
          }}>
            <ErrorBoundary type="image">
              <Avatar 
                src={profilePhoto}
                alt={memorial.name}
                sx={{ 
                  width: { xs: 120, sm: 150 }, 
                  height: { xs: 120, sm: 150 }, 
                  border: '2px solid #e0e0e0',
                  mr: { xs: 0, sm: 4 },
                  mb: { xs: 2, sm: 0 }
                }}
              />
            </ErrorBoundary>
            
            <Box sx={{ flexGrow: 1, textAlign: { xs: 'center', sm: 'left' } }}>
              <Typography variant="h1" fontSize="1.5rem" fontWeight="bold">
                {memorial.name}
              </Typography>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {formatDate(memorial.dateOfBirth)} - {formatDate(memorial.dateOfDeath)}
              </Typography>
              
              <Box sx={{ display: 'flex', mt: 1, justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                <Box sx={{ mr: 3, textAlign: 'center' }}>
                  <Typography variant="h6" fontWeight="bold">{photos.length}</Typography>
                  <Typography variant="body2" color="text.secondary">Photos</Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Biography Section */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h2" fontSize="1.25rem" fontWeight="bold" sx={{ mb: 1 }}>Biography</Typography>
            <Typography 
              variant="body1" 
              paragraph 
              sx={{ 
                whiteSpace: 'pre-line',
                lineHeight: 1.7
              }}
            >
              {memorial.biography}
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Photos Gallery */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="h2" fontSize="1.25rem" fontWeight="bold" sx={{ mb: 2 }}>Photos</Typography>
            
            {!imagesPreloaded && (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" sx={{ mt: 1 }}>Loading photos...</Typography>
              </Box>
            )}
            
            {imagesPreloaded && photos && photos.length > 0 ? (
              <Grid container spacing={2}>
                {photos.map((photo, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <ErrorBoundary type="image">
                      <Card sx={{ 
                        height: 0, 
                        paddingTop: '100%', 
                        position: 'relative',
                        borderRadius: 1,
                        boxShadow: 2,
                        overflow: 'hidden'
                      }}>
                        <Box sx={{ 
                          position: 'absolute', 
                          top: 0, 
                          left: 0, 
                          width: '100%', 
                          height: '100%' 
                        }}>
                          <ImageWithRetry
  src={photo.url}
  alt={photo.description || `Photo of ${memorial.name}`}
  loading={index > 5 ? "lazy" : "eager"}
  width="300"
  height="300"
  onError={(e, src) => {
    logError('Image', 'Failed to load image after retries', {
      url: src,
      photoIndex: index
    });
  }}
  sx={{ 
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  }}
                          />
                        </Box>
                      </Card>
                      {photo.description && (
                        <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          sx={{ 
                            mt: 0.5, 
                            textAlign: 'center',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical'
                          }}
                        >
                          {photo.description}
                        </Typography>
                      )}
                    </ErrorBoundary>
                  </Grid>
                ))}
              </Grid>
            ) : imagesPreloaded && (
              <Box sx={{ 
                textAlign: 'center', 
                py: 4,
                bgcolor: 'rgba(0, 0, 0, 0.02)',
                borderRadius: 1
              }}>
                <Typography variant="body1" color="text.secondary">
                  No photos available
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Container>
    </ErrorBoundary>
  );
};

export default ViewMemorial;